import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, FileText, BarChart3, Clock, Upload, Eye } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { StatCard, AnalisiCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { ROUTE_PATHS } from '@/lib/index';
import type { Analisi } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';

const Dashboard = () => {
  const { profile } = useAuth();
  const [numCondomini, setNumCondomini] = useState(0);
  const [numDocumenti, setNumDocumenti] = useState(0);
  const [numAnalisiCompletate, setNumAnalisiCompletate] = useState(0);
  const [numInElaborazione, setNumInElaborazione] = useState(0);
  const [recentAnalisi, setRecentAnalisi] = useState<Analisi[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [condoRes, docRes, analisiCompRes, elaborazioneRes] = await Promise.all([
          supabase.from('condomini').select('*', { count: 'exact', head: true }),
          supabase.from('documenti').select('*', { count: 'exact', head: true }),
          supabase.from('analisi').select('*', { count: 'exact', head: true }).eq('stato', 'completata'),
          supabase.from('analisi').select('*', { count: 'exact', head: true }).in('stato', ['elaborazione', 'bozza']),
        ]);

        setNumCondomini(condoRes.count || 0);
        setNumDocumenti(docRes.count || 0);
        setNumAnalisiCompletate(analisiCompRes.count || 0);
        setNumInElaborazione(elaborazioneRes.count || 0);

        const { data: analisiData } = await supabase
          .from('analisi')
          .select(`
            *,
            condomini!inner(nome)
          `)
          .eq('stato', 'completata')
          .order('created_at', { ascending: false })
          .limit(5);

        if (analisiData) {
          const mappedAnalisi = analisiData.map((item: any) => ({
            ...item,
            nome_condominio: item.condomini?.nome || 'Sconosciuto',
          }));
          setRecentAnalisi(mappedAnalisi);
        }
      } catch (error) {
        console.error('Errore caricamento dati:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 35,
      },
    },
  };

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 35 }}
        >
          <h1 className="text-3xl font-bold text-foreground">
            Benvenuto, {profile?.full_name || 'Utente'}
          </h1>
          <p className="text-muted-foreground mt-2">
            Panoramica della tua attività di gestione condominiale
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <StatCard
              title="Condomini"
              value={loading ? '...' : numCondomini}
              icon={Building2}
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <StatCard
              title="Documenti Caricati"
              value={loading ? '...' : numDocumenti}
              icon={FileText}
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <StatCard
              title="Analisi Completate"
              value={loading ? '...' : numAnalisiCompletate}
              icon={BarChart3}
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <StatCard
              title="In Elaborazione"
              value={loading ? '...' : numInElaborazione}
              icon={Clock}
            />
          </motion.div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-semibold">Analisi Recenti</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : recentAnalisi.length > 0 ? (
                  <div className="space-y-4">
                    {recentAnalisi.map((analisi) => (
                      <AnalisiCard
                        key={analisi.id}
                        analisi={analisi}
                        onView={() => window.location.href = `#${ROUTE_PATHS.ANALISI}/${analisi.id}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessuna analisi completata</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-semibold">Azioni Rapide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Link to={ROUTE_PATHS.DOCUMENTI}>
                  <Button className="w-full justify-start" size="lg" variant="outline">
                    <Upload className="mr-3 h-5 w-5" />
                    <div className="text-left">
                      <p className="font-semibold">Carica Documento</p>
                      <p className="text-xs text-muted-foreground">
                        Aggiungi un nuovo consuntivo o preventivo
                      </p>
                    </div>
                  </Button>
                </Link>

                <Link to={ROUTE_PATHS.CONDOMINI}>
                  <Button className="w-full justify-start" size="lg" variant="outline">
                    <Building2 className="mr-3 h-5 w-5" />
                    <div className="text-left">
                      <p className="font-semibold">Gestisci Condomini</p>
                      <p className="text-xs text-muted-foreground">
                        Visualizza e modifica i tuoi condomini
                      </p>
                    </div>
                  </Button>
                </Link>

                <Link to={ROUTE_PATHS.ANALISI}>
                  <Button className="w-full justify-start" size="lg" variant="outline">
                    <Eye className="mr-3 h-5 w-5" />
                    <div className="text-left">
                      <p className="font-semibold">Visualizza Analisi</p>
                      <p className="text-xs text-muted-foreground">
                        Esplora tutte le analisi completate
                      </p>
                    </div>
                  </Button>
                </Link>

                <div className="pt-4 border-t border-border">
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <BarChart3 className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-foreground">
                          Analisi AI Professionale
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Carica i tuoi documenti PDF e ottieni analisi dettagliate con
                          grafici interattivi e note di sintesi automatiche.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;